import type { CourierWithExtraInfo } from 'common/hooks/useMergeCourierExtraInfo';

import {
  addDays,
  type Day,
  endOfDay,
  endOfMonth,
  endOfWeek,
  formatISO,
  isMonday,
  previousMonday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { z } from 'zod';

import { type Area, CurrencyCode } from 'common/api/api-types';
import toast from 'common/components/Toast';
import { MONDAY } from 'common/constants';
import currencyCodeToSymbol from 'common/currencyCodeToSymbol';
import { createTextByDelimiterSeparatedValues, downloadFile } from 'common/utils/fileUtils';
import { type CsvColumn, getMaxWeeklyHours, getName, getTeamName } from 'common/utils/reportUtils';
import { idsCsv } from 'features/earnings/constants';
import {
  calculateTaskRelated,
  calculateTotalEarnings,
  defaultIds,
  EXCLUDED_TRANSACTIONS,
  getAdjustments,
  getBonusCost,
  getDphBonusCost,
  getShiftGuarantee,
  getTips,
  getUnknown,
  getUnspecified,
  TASK_RELATED_TRANSACTIONS,
} from 'features/earnings/useAggregatedColumns';

const MonetaryValue: Record<CurrencyCode, (value: number) => number> = {
  [CurrencyCode.parse('JPY')]: value => value,
  [CurrencyCode.parse('ISK')]: value => value,
};

export const defaultMonetaryValue = (value: number) => value / 100;

export const getAmountToDisplay = ({
  decimalFormat,
  amount,
  currency,
}: {
  decimalFormat?: Intl.NumberFormat;
  amount?: number;
  currency?: CurrencyCode;
}) => {
  const getValue = currency ? MonetaryValue[currency] : undefined;

  const result =
    decimalFormat && amount !== undefined
      ? (currencyCodeToSymbol(currency) ?? '') +
        decimalFormat.format((getValue || defaultMonetaryValue)(amount))
      : '-';
  return result;
};

export const Timeframe = z.enum([
  'last30Days',
  'last7Days',
  'previousMonth',
  'previousWeek',
  'thisMonthFrom1st',
  'thisWeekFromMonday',
  'today',
  'yesterday',
  'previousMonthFirstCycle',
  'previousMonthSecondCycle',
  'thisMonthFirstCycle',
  'thisMonthSecondCycle',
  'custom',
]);

export type Timeframe = z.infer<typeof Timeframe>;

export const DEFAULT_TIMEFRAME: Timeframe = 'today';

export const getDateRangeByTimeframe = ({
  now,
  timeframe = DEFAULT_TIMEFRAME,
  weekStartsOn = MONDAY,
}: {
  now: Date;
  timeframe?: Timeframe;
  weekStartsOn?: Day;
}) => {
  switch (timeframe ?? DEFAULT_TIMEFRAME) {
    case 'last30Days':
      break;
    case 'last7Days': {
      return {
        from: startOfDay(subDays(now, 7)),
        to: endOfDay(subDays(now, 1)),
      };
    }
    case 'previousWeek': {
      const previousWeek = subWeeks(now, 1);
      return {
        from: startOfWeek(previousWeek, { weekStartsOn }),
        to: endOfWeek(previousWeek, { weekStartsOn }),
      };
    }
    case 'thisMonthFrom1st': {
      return {
        from: startOfMonth(now),
        to: endOfDay(now),
      };
    }
    case 'thisWeekFromMonday': {
      const mondayThisWeek = isMonday(now) ? now : previousMonday(now);
      return {
        from: startOfDay(mondayThisWeek),
        to: endOfDay(now),
      };
    }
    case 'today': {
      return {
        from: startOfDay(now),
        to: endOfDay(now),
      };
    }
    case 'yesterday': {
      const yesterday = subDays(now, 1);
      return {
        from: startOfDay(yesterday),
        to: endOfDay(yesterday),
      };
    }
    case 'previousMonth': {
      const previousMonth = subMonths(now, 1);
      return {
        from: startOfMonth(previousMonth),
        to: endOfMonth(previousMonth),
      };
    }
    case 'previousMonthFirstCycle': {
      const previousMonth = subMonths(now, 1);
      return {
        from: startOfMonth(previousMonth),
        to: endOfDay(addDays(startOfMonth(previousMonth), 14)),
      };
    }
    case 'previousMonthSecondCycle': {
      const previousMonth = subMonths(now, 1);
      return {
        from: startOfDay(addDays(startOfMonth(previousMonth), 15)),
        to: endOfMonth(previousMonth),
      };
    }
    case 'thisMonthFirstCycle': {
      return {
        from: startOfMonth(now),
        to: endOfDay(addDays(startOfMonth(now), 14)),
      };
    }
    case 'thisMonthSecondCycle': {
      return {
        from: startOfDay(addDays(startOfMonth(now), 15)),
        to: endOfMonth(now),
      };
    }
  }

  return {
    from: startOfDay(subDays(now, 30)),
    to: endOfDay(subDays(now, 1)),
  };
};

const getCellValue = ({
  id,
  courier,
  areas,
  decimalFormat,
}: {
  decimalFormat?: Intl.NumberFormat;
  id: string;
  courier: CourierWithExtraInfo;
  areas?: Area[];
}) => {
  switch (id) {
    case idsCsv.onlyName:
      return getName(courier.preferredName);
    case idsCsv.phone:
      return courier.phone ?? '-';
    case idsCsv.email:
      return courier.email;
    case idsCsv.onlyId:
      return courier.id;
    case idsCsv.team:
      return getTeamName(courier.deliveryArea, areas) ?? '-';
    case idsCsv.maxWeeklyHours:
      return getMaxWeeklyHours(courier.additionalFields.maxWeeklyHours?.value);
    case defaultIds.totalAmountWithoutTip: {
      const { earnings } = courier._extra;

      return getAmountToDisplay({
        decimalFormat,
        amount: calculateTotalEarnings({
          transactions: earnings?.aggregatedTransactions,
          excludedTransactionTypes: ['transaction_type_tip', ...EXCLUDED_TRANSACTIONS],
        }),
        currency: earnings?.aggregatedTransactions[0]?.currency,
      });
    }
    case defaultIds.totalAmountWithTip: {
      const { earnings } = courier._extra;

      return getAmountToDisplay({
        decimalFormat,
        amount: calculateTotalEarnings({
          transactions: earnings?.aggregatedTransactions,
          excludedTransactionTypes: EXCLUDED_TRANSACTIONS,
        }),
        currency: earnings?.aggregatedTransactions[0]?.currency,
      });
    }
    case defaultIds.transaction_type_tip:
      return getAmountToDisplay({
        ...(getTips(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
    case defaultIds.transaction_type_manual:
      return getAmountToDisplay({
        ...(getAdjustments(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
    case defaultIds.transaction_type_shift_guarantee:
      return getAmountToDisplay({
        ...(getShiftGuarantee(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
    case defaultIds.taskRelated: {
      const { earnings } = courier._extra;

      return getAmountToDisplay({
        decimalFormat,
        amount: calculateTaskRelated({
          transactions: earnings?.aggregatedTransactions,
          includedTransactionTypes: TASK_RELATED_TRANSACTIONS,
        }),
        currency: earnings?.aggregatedTransactions[0]?.currency,
      });
    }
    case defaultIds.transaction_type_bonus_cost:
      return getAmountToDisplay({
        ...(getBonusCost(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
    case defaultIds.transaction_type_dph_bonus_cost:
      return getAmountToDisplay({
        ...(getDphBonusCost(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
    case defaultIds.transaction_type_unspecified:
      return getAmountToDisplay({
        ...(getUnspecified(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
    case defaultIds.unknown:
      return getAmountToDisplay({
        ...(getUnknown(courier._extra.earnings?.aggregatedTransactions) ?? {}),
        decimalFormat,
      });
  }

  return '-';
};

export const downloadAsCsv = ({
  areas,
  columns,
  dataSource,
  decimalFormat,
}: {
  areas?: Area[];
  columns: Array<CsvColumn>;
  dataSource: CourierWithExtraInfo[];
  decimalFormat?: Intl.NumberFormat;
}) => {
  toast({
    title: 'Exporting',
    content: "Don't close browser until completed",
    severity: 'info',
  });

  // e.g. export_financials_20240709T134641+03_00.csv
  const filename = `export_financials_${formatISO(new Date(), { format: 'basic' })}.csv`;

  const fileContent = createTextByDelimiterSeparatedValues([
    columns.map(column => column.name),
    ...dataSource.map(courier =>
      columns.map(column => getCellValue({ decimalFormat, id: column.id, courier, areas })),
    ),
  ]);

  downloadFile([fileContent], filename, 'text/csv');

  toast({
    title: 'Export completed',
    content: 'Your report is ready',
    severity: 'success',
  });
};
